import { useEffect, useState } from 'react'
import { Text } from '../../../components'
import { CrmRequest, ensureOk, getErrorMessage } from '../api'
import { ListStatus, Pager } from '../components'

const PAGE_SIZE = 10
interface Activity {
    total: number;
    has_next: boolean;
    chart_days: number;
    summary: {clicks: number; active_links: number; active_stores: number; stores: number; card_open: number; map_open: number};
    metrics: {date: string; count: number}[];
    links: {id: number; store_id: number; store_title: string; label: string; link: string; clicks: number; total_clicks: number}[];
}
const number = (value: number) => value.toLocaleString('ru-RU')

export const MetricsTab = ({request}: {request: CrmRequest}) => {
    const [days, setDays] = useState(0)
    const [offset, setOffset] = useState(0)
    const [revision, setRevision] = useState(0)
    const [data, setData] = useState<Activity | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const controller = new AbortController()
        let active = true
        setLoading(true)
        setError(null)
        const load = async () => {
            try {
                const params = new URLSearchParams({days: String(days), offset: String(offset), limit: String(PAGE_SIZE)})
                const res = await request(`/crm/all-metrics?${params}`, {signal: controller.signal})
                if (!res) return
                await ensureOk(res, 'Не удалось загрузить активность')
                const result: Activity = await res.json()
                if (!active) return
                if (offset > 0 && offset >= result.total) {
                    setOffset(Math.max(0, Math.ceil(result.total / PAGE_SIZE) - 1) * PAGE_SIZE)
                } else setData(result)
            } catch (err) {
                if (active) setError(getErrorMessage(err, 'Не удалось загрузить активность'))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false; controller.abort() }
    }, [request, days, offset, revision])

    const max = Math.max(1, ...(data?.metrics.map((point) => point.count) ?? []))
    return (
        <div className="crm-list">
            <div className="crm-activity-toolbar">
                <label>
                    <Text size="xs" color="lightGray">Период активности</Text>
                    <select value={days} onChange={(event) => { setDays(Number(event.target.value)); setOffset(0) }}>
                        <option value={0}>За всё время</option>
                        <option value={7}>Последние 7 дней</option>
                        <option value={30}>Последние 30 дней</option>
                        <option value={90}>Последние 90 дней</option>
                    </select>
                </label>
                <button type="button" className="admin-edit-btn" disabled={loading} onClick={() => setRevision((value) => value + 1)}>
                    <Text size="xs" color="lightGray">Обновить</Text>
                </button>
            </div>
            <ListStatus loading={loading} error={error} empty={false} emptyText="" />
            {!loading && !error && data && <>
                <div className="crm-activity-summary">
                    {[
                        ['Переходы по ссылкам', data.summary.clicks],
                        ['Активные ссылки', data.summary.active_links],
                        ['Страницы с переходами', data.summary.active_stores],
                        ['Всего страниц', data.summary.stores],
                        ['Открытия карточек · всё время', data.summary.card_open],
                        ['Открытия карты · всё время', data.summary.map_open],
                    ].map(([label, value]) => (
                        <div key={label} className="crm-activity-stat">
                            <Text size="l" color="white">{number(Number(value))}</Text>
                            <Text size="xs" color="lightGray">{String(label)}</Text>
                        </div>
                    ))}
                </div>
                <Text size="xs" color="lightGray">Счётчики событий по всем существующим страницам, не уникальных посетителей сайта. Период применяется к переходам по ссылкам. Даты — по времени сервера.</Text>
                <Text size="s" color="white">{`Динамика переходов · последние ${data.chart_days} дней`}</Text>
                <div className="crm-activity-chart" aria-label="Переходы по дням">
                    {data.metrics.map((point) => (
                        <div key={point.date} className="crm-activity-day" tabIndex={0} title={`${point.date}: ${number(point.count)}`} aria-label={`${point.date}: ${number(point.count)} переходов`}>
                            <span style={{height: `${Math.max(point.count ? 2 : 0, point.count / max * 100)}%`}} />
                        </div>
                    ))}
                </div>
                <div className="crm-activity-dates"><span>{data.metrics[0]?.date}</span><span>{data.metrics[data.metrics.length - 1]?.date}</span></div>
                <Text size="s" color="white">{`Популярные ссылки · всего ${number(data.total)}`}</Text>
                <Text size="xs" color="lightGray">По убыванию переходов за выбранный период, по 10 на странице.</Text>
                {data.links.length === 0 && <Text size="s" color="lightGray">Ссылок пока нет</Text>}
                {data.links.map((link, index) => (
                    <div className="crm-activity-link" key={link.id}>
                        <span className="crm-activity-rank">{offset + index + 1}</span>
                        <div className="crm-activity-link-main">
                            <Text size="s" color="white">{link.label || 'Без названия'}</Text>
                            <a href={link.link} target="_blank" rel="noopener noreferrer">{link.link}</a>
                            <a href={`/${link.store_id}`} target="_blank" rel="noopener noreferrer">{`${link.store_title || 'Без названия'} · #${link.store_id}`}</a>
                        </div>
                        <div className="crm-activity-count"><Text size="m" color="white">{number(link.clicks)}</Text><Text size="xs" color="lightGray">переходов</Text></div>
                    </div>
                ))}
                <Pager offset={offset} hasNext={data.has_next} loading={loading} onChange={setOffset} pageSize={PAGE_SIZE} />
            </>}
        </div>
    )
}
